import { Injectable } from '@angular/core';
import { isNullOrUndefined, RuleValueConstants } from '@uzurv/core';
import { RuleValue } from '@uzurv/clara';

@Injectable({
  providedIn: 'root'
})
export class RuleValueService {
  private _ruleValues: Array<RuleValue> = [];

  /**
   * Rule values are currently not fetched, since the backend endpoint is locked down with the "system" permission.
   */
  getRuleValues(): Array<RuleValue> {
    return this._ruleValues;
  }

  /**
   * Generic method to get a rule value with automatic type conversion
   */
  private getRuleValueByName<T>(name: string, defaultValue: T): T {
    const ruleValues = this.getRuleValues();
    if (isNullOrUndefined(ruleValues)) {
      return defaultValue;
    }

    const ruleValue = ruleValues.find(rv => rv.name === name);
    if (isNullOrUndefined(ruleValue)) {
      return defaultValue;
    }

    return this.parseValue<T>(ruleValue.value, defaultValue);
  }

  /**
   * Parses a string value to the appropriate type based on the default value type
   */
  private parseValue<T>(value: string, defaultValue: T): T {
    if (isNullOrUndefined(value)) {
      return defaultValue;
    }

    const defaultType = typeof defaultValue;

    switch (defaultType) {
      case 'number': {
        const parsed = Number(value);
        return (isNaN(parsed) ? defaultValue : parsed) as T;
      }

      case 'boolean': {
        const trimmedValue = value.trim();
        if (trimmedValue === 'true') {
          return true as T;
        }
        if (trimmedValue === 'false') {
          return false as T;
        }
        return defaultValue;
      }

      case 'string':
      default:
        return value as T;
    }
  }

  getAdjustedProviderCommissionWarningThresholdPercentage(): number {
    return this.getRuleValueByName(
      RuleValueConstants.ADJUSTED_PROVIDER_COMMISSION_WARNING_THRESHOLD_PERCENTAGE,
      RuleValueConstants.ADJUSTED_PROVIDER_COMMISSION_WARNING_THRESHOLD_PERCENTAGE_DEFAULT
    );
  }

  getAdjustedProviderCommissionWarningThresholdTotalDefault(): number {
    return this.getRuleValueByName(
      RuleValueConstants.ADJUSTED_PROVIDER_COMMISSION_WARNING_THRESHOLD_TOTAL,
      RuleValueConstants.ADJUSTED_PROVIDER_COMMISSION_WARNING_THRESHOLD_TOTAL_DEFAULT
    );
  }

  getAdjustedProviderCommissionBlockingThresholdPercentageDefault(): number {
    return this.getRuleValueByName(
      RuleValueConstants.ADJUSTED_PROVIDER_COMMISSION_BLOCKING_THRESHOLD_PERCENTAGE,
      RuleValueConstants.ADJUSTED_PROVIDER_COMMISSION_BLOCKING_THRESHOLD_PERCENTAGE_DEFAULT
    );
  }

  getAdjustedProviderCommissionBlockingThresholdTotalDefault(): number {
    return this.getRuleValueByName(
      RuleValueConstants.ADJUSTED_PROVIDER_COMMISSION_BLOCKING_THRESHOLD_TOTAL,
      RuleValueConstants.ADJUSTED_PROVIDER_COMMISSION_BLOCKING_THRESHOLD_TOTAL_DEFAULT
    );
  }

  getAdjustedWavProviderCommissionWarningThresholdPercentageDefault(): number {
    return this.getRuleValueByName(
      RuleValueConstants.ADJUSTED_WAV_PROVIDER_COMMISSION_WARNING_THRESHOLD_PERCENTAGE,
      RuleValueConstants.ADJUSTED_WAV_PROVIDER_COMMISSION_WARNING_THRESHOLD_PERCENTAGE_DEFAULT
    );
  }

  getAdjustedWavProviderCommissionWarningThresholdTotalDefault(): number {
    return this.getRuleValueByName(
      RuleValueConstants.ADJUSTED_WAV_PROVIDER_COMMISSION_WARNING_THRESHOLD_TOTAL,
      RuleValueConstants.ADJUSTED_WAV_PROVIDER_COMMISSION_WARNING_THRESHOLD_TOTAL_DEFAULT
    );
  }

  getAdjustedWavProviderCommissionBlockingThresholdPercentageDefault(): number {
    return this.getRuleValueByName(
      RuleValueConstants.ADJUSTED_WAV_PROVIDER_COMMISSION_BLOCKING_THRESHOLD_PERCENTAGE,
      RuleValueConstants.ADJUSTED_WAV_PROVIDER_COMMISSION_BLOCKING_THRESHOLD_PERCENTAGE_DEFAULT
    );
  }

  getAdjustedWavProviderCommissionBlockingThresholdTotalDefault(): number {
    return this.getRuleValueByName(
      RuleValueConstants.ADJUSTED_WAV_PROVIDER_COMMISSION_BLOCKING_THRESHOLD_TOTAL,
      RuleValueConstants.ADJUSTED_WAV_PROVIDER_COMMISSION_BLOCKING_THRESHOLD_TOTAL_DEFAULT
    );
  }
}
